import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { endOfMonth, endOfWeek, isAfter, startOfMonth, startOfWeek } from 'date-fns';

import { DateControls } from 'components/DateControls';
import Message from 'components/Message';
import { TooltipContent } from 'components/Tooltip';
import { selectActiveVenue, selectActiveVenueTimezone, selectSchedulesStartDate } from 'selectors/general';

import ScheduleCalendarDayButton, { UNSYNCED_TOOLTIP_ID } from './ScheduleCalendarDayButton';
import ScheduleCalendarLegend from './ScheduleCalendarLegend';
import messages from './ScheduleCalendar.messages';
import { useCalendarIndicators } from './ScheduleCalendar.hooks';
import {
  buildDayModifiers,
  DAY_INDICATOR_CLASS_NAMES,
  getDisabledAfterDate,
  getIndicatorFetchRange,
  getSelectedDate,
} from './ScheduleCalendar.helpers';

const CALENDAR_COMPONENTS = { DayButton: ScheduleCalendarDayButton };

const ScheduleCalendarControl = ({ setDate }) => {
  const startDate = useSelector(selectSchedulesStartDate);
  const timeZone = useSelector(selectActiveVenueTimezone);
  const { isManualStudio } = useSelector(selectActiveVenue);

  // The selector can hand back a new Date instance on every render, so the effect keys off the
  // month's timestamp — otherwise it would reset the month the user just navigated to.
  const selectedMonthTime = startOfMonth(getSelectedDate({ startDate, timeZone })).getTime();
  const [month, setMonth] = useState(() => new Date(selectedMonthTime));

  useEffect(() => {
    setMonth(new Date(selectedMonthTime));
  }, [selectedMonthTime]);

  const range = getIndicatorFetchRange({
    visibleStart: startOfWeek(startOfMonth(month)),
    visibleEnd: endOfWeek(endOfMonth(month)),
    timeZone,
  });

  const { data: dayIndicators = {} } = useCalendarIndicators(range || {});

  const { noClasses, waiting } = buildDayModifiers({ dayIndicators, timeZone });
  const disabledAfter = getDisabledAfterDate({ timeZone, isManualStudio });
  const modifiers = { noClasses, waiting, ...(disabledAfter ? { disabled: { after: disabledAfter } } : {}) };
  const isValidDate = disabledAfter ? date => !isAfter(date, disabledAfter) : undefined;

  return (
    <>
      <DateControls
        isControl
        setDate={setDate}
        components={CALENDAR_COMPONENTS}
        isValidDate={isValidDate}
        modifiers={modifiers}
        modifiersClassNames={DAY_INDICATOR_CLASS_NAMES}
        month={month}
        onMonthChange={setMonth}
        pickerFooter={<ScheduleCalendarLegend />}
      />
      {!isManualStudio && (
        <TooltipContent name={UNSYNCED_TOOLTIP_ID} className="schedule-calendar-tooltip" place="top" clickable>
          <Message message={messages.unsyncedTooltip} />
        </TooltipContent>
      )}
    </>
  );
};

ScheduleCalendarControl.propTypes = {
  setDate: PropTypes.func,
};

export default ScheduleCalendarControl;
